import { z } from "zod";
import { MONTHLY_WEEKLY_ACTIVATION } from "./f04-field-class";
import { FORM_DISPLAY_NAMES } from "./form-display-names";
import { MEASUREMENT_SUGGESTION_WINDOW_DAYS } from "./health";
import { measurementSourceLabel } from "./measurement-source";
import { bloodPressurePulseProvenance } from "./measurement-review-journal";
import {
  F04_SYMPTOM_GATE_KEY,
  F04_SYMPTOM_JOURNAL_FIELDS,
  F05_SYMPTOM_JOURNAL_FIELDS,
  f04SymptomSentences,
  f05SymptomSentences,
  JOURNAL_SECTION_MAX,
  isDeclinedAnswer,
  journalAnswerLine,
  meetingJournalSectionSchema,
  prefillJournalSectionLines,
  type JournalLayout,
  type JournalPrefillSource,
  type JournalSectionKey,
} from "./meeting-journal";
import {
  journalStodordSchema,
  journalWebdocStatusSchema,
  type JournalPrefillLine,
  type JournalPrefillSourceRef,
} from "./journal-provenance";
import { journalFieldPhrase, journalPhrasedLine } from "./journal-phrasing";
import {
  journalRiskFlagLine,
  type JournalRiskFlag,
} from "./journal-evidence-lines";
import { prescriptionLabel } from "./prescription-cycle";

// TES-978 — the monthly review's journal note gets its OWN headings.
//
// #992 (TES-904 step 4) gave it the care meeting's seven, because the epic
// named no headings for the monthly review. Emelie's TES-978 does, verbatim:
//   "Månadsreviewns sektioner är minst:"
//   "Aktuellt/patientens upplevelse · Vikt · Midjemått · Blodtryck ·"
//   "Psykisk status och beteende · Biverkningar · Aktuell behandling och dos ·"
//   "Bedömning · Åtgärd och beslut · Nästa uppföljning"
// The box model, the saving and "Sammanställ journal" are the meeting's
// (JournalLayout); only the headings and their sources differ.
//
// TES-984 — Emelie's answers 2026-09-28 (Ö-265..268) replace #1021's
// assumptions:
//   - "Ärftlighet och sjukdomar" stays out of the monthly note; new diagnoses,
//     medicines and acute visits go under the current-state, assessment
//     and decision headings.
//   - "Livsstil" comes back as a compact, editable heading, prefilled from the
//     SHARED weekly answers and the monthly check-in (F-05), always phrased as
//     what the patient reports. It sits right after the measurements and
//     before "Psykisk status och beteende": the two read the same answers
//     (hunger, satiety, cravings, daily function) from two angles.
//   - "Psykisk status och beteende" is pre-sorted from those answers too.
//   - Both say "Saknas/ej bedömd" when there is nothing to go on
//     (missingMarkerSections), never a normal finding.

// The key is what is stored; the heading is what the doctor and Webdoc read.
// Renaming a heading is a copy change here; renaming a key is a migration.
export const MONTHLY_JOURNAL_SECTION_KEYS = [
  "aktuellt_upplevelse",
  "vikt",
  "midjematt",
  "blodtryck",
  // TES-984 — not the meeting's `livsstil` key: #992's rows under that key
  // sit in the same table (MONTHLY_LEGACY_SECTION_TARGET explains why).
  "livsstil_manad",
  "psykisk_status",
  "biverkningar",
  "aktuell_behandling_dos",
  "bedomning",
  "atgard_beslut",
  "nasta_uppfoljning",
] as const;
export const monthlyJournalSectionKeySchema = z.enum(
  MONTHLY_JOURNAL_SECTION_KEYS,
);
export type MonthlyJournalSectionKey = z.infer<
  typeof monthlyJournalSectionKeySchema
>;

export const MONTHLY_JOURNAL_SECTION_HEADINGS: Record<
  MonthlyJournalSectionKey,
  string
> = {
  aktuellt_upplevelse: "Aktuellt/patientens upplevelse",
  vikt: "Vikt",
  midjematt: "Midjemått",
  blodtryck: "Blodtryck",
  livsstil_manad: "Livsstil",
  psykisk_status: "Psykisk status och beteende",
  biverkningar: "Biverkningar",
  aktuell_behandling_dos: "Aktuell behandling och dos",
  bedomning: "Bedömning",
  atgard_beslut: "Åtgärd och beslut",
  nasta_uppfoljning: "Nästa uppföljning",
};

export const MONTHLY_JOURNAL_LAYOUT: JournalLayout<MonthlyJournalSectionKey> = {
  keys: MONTHLY_JOURNAL_SECTION_KEYS,
  headings: MONTHLY_JOURNAL_SECTION_HEADINGS,
  // "Sammanställ journal" writes the order on screen as the first line of
  // the decision box, as the meeting does into its plan box (TES-956).
  orderSection: "atgard_beslut",
  // TES-1058 — the decision's lines (journal-decision.ts), after the order.
  assessmentSection: "bedomning",
  planSection: "atgard_beslut",
  // TES-978 — the follow-up decision leads "Nästa uppföljning" the same way.
  followUpSection: "nasta_uppfoljning",
  // TES-984 — "saknas/ej bedömd" when empty (Ö-265, Ö-267).
  // TES-1453 (Ö-735) — "Biverkningar" too: "Saknas/ej bedömd" when nothing
  // was reported, never "inga".
  missingMarkerSections: ["livsstil_manad", "psykisk_status", "biverkningar"],
};

/** 1-based position of a heading in the monthly note. */
export function monthlyJournalSectionOrder(
  key: MonthlyJournalSectionKey,
): number {
  return MONTHLY_JOURNAL_SECTION_KEYS.indexOf(key) + 1;
}

// The doctor's own boxes: never prefilled. The assessment, the decision and
// the next follow-up are his decisions. ("Psykisk status och beteende" was
// here until TES-984: Emelie now has it pre-sorted from her answers, for him
// to edit and assess.)
export const MONTHLY_DOCTOR_ONLY_SECTIONS: readonly MonthlyJournalSectionKey[] =
  ["bedomning", "atgard_beslut", "nasta_uppfoljning"];

export const monthlyReviewJournalSectionSchema =
  meetingJournalSectionSchema.extend({ key: monthlyJournalSectionKeySchema });
export type MonthlyReviewJournalSection = z.infer<
  typeof monthlyReviewJournalSectionSchema
>;

export const monthlyReviewJournalSchema = z.object({
  cycleId: z.string().uuid(),
  sections: z.array(monthlyReviewJournalSectionSchema),
  // The doctor's shorthand for the note, as last autosaved (TES-978).
  stodord: journalStodordSchema.nullable().optional(),
  // TES-978 — which final version is marked "Införd i Webdoc", if any.
  webdoc: journalWebdocStatusSchema.optional(),
});
export type MonthlyReviewJournal = z.infer<typeof monthlyReviewJournalSchema>;

// PUT …/cycles/:cycleId/journal — the boxes the doctor changed.
export const updateMonthlyReviewJournalSchema = z.object({
  sections: z
    .array(
      z.object({
        key: monthlyJournalSectionKeySchema,
        content: z.string().max(JOURNAL_SECTION_MAX),
      }),
    )
    .min(1)
    .max(MONTHLY_JOURNAL_SECTION_KEYS.length)
    .refine((s) => new Set(s.map((x) => x.key)).size === s.length, {
      message: "duplicate section key",
    }),
});
export type UpdateMonthlyReviewJournal = z.infer<
  typeof updateMonthlyReviewJournalSchema
>;

// ---- rows written under the old headings (#992) ---------------------------

// From #992 until this change the monthly note was stored under the meeting's
// seven keys, on QA and prod. Those rows are never deleted or rewritten; on
// the first read under the new headings their text is CARRIED into the new
// boxes (the ciphertext is bound to its row, so this cannot be done in SQL —
// the same reason the meeting carries `Meeting.assessment` in code).
//
//   aktuellt               → aktuellt_upplevelse     (same meaning)
//   aktuella_lakemedel     → aktuell_behandling_dos  (same meaning)
//   bedomning              → bedomning               (same key: nothing to do)
//   atgard_behandlingsplan → atgard_beslut           (same meaning)
//   status                 → vikt                    (#992 prefilled only her weight there)
//   arftlighet_sjukdomar   → aktuellt_upplevelse     (no counterpart: appended, headed)
//   livsstil               → livsstil_manad          (same meaning, TES-984)
//
// TES-984 — #1021 (live on QA and prod from 2026-09-28) appended `livsstil`
// to Aktuellt, because the note then had no Livsstil. Emelie wants it under
// Livsstil. The new heading has its own key, so for a note #1021 has not
// opened yet the old row is moved into it like any "same meaning" row. For a
// note #1021 already opened, the text is ALREADY in Aktuellt, under
// "Tidigare under rubriken ”Livsstil”", with the doctor's name on the box;
// carrying it again would print it twice, and taking it out of Aktuellt would
// rewrite a box the doctor owns without him. So it is left where it is, and
// the new Livsstil box starts from the prefill (`existingKeys` below). Nothing
// is lost either way: the old row itself is never touched.
//
// "Same meaning" moves the text as it stands, with its author and time, so a
// box the doctor wrote in (or emptied) stays his and is never prefilled over.
// Everything else is APPENDED under a line naming the heading it was written
// under, because the doctor wrote it for a different heading and the reader
// must be able to see that. A doctor-written "Status" is appended too: #992
// put only the weight there, but a doctor may have added a blood pressure.
//
// A box nobody wrote in (#992's own prefill) is carried only for a review that
// is already decided, where nothing is prefilled any more and it is the only
// record of what the doctor saw. While the review is open, the new prefill
// rebuilds it from the same sources, with more of them.
export const MONTHLY_LEGACY_SECTION_TARGET: Readonly<
  Record<
    string,
    { to: MonthlyJournalSectionKey; direct: boolean; heading: string }
  >
> = {
  aktuellt: { to: "aktuellt_upplevelse", direct: true, heading: "Aktuellt" },
  aktuella_lakemedel: {
    to: "aktuell_behandling_dos",
    direct: true,
    heading: "Aktuella läkemedel",
  },
  atgard_behandlingsplan: {
    to: "atgard_beslut",
    direct: true,
    heading: "Åtgärd och behandlingsplan",
  },
  status: { to: "vikt", direct: false, heading: "Status" },
  arftlighet_sjukdomar: {
    to: "aktuellt_upplevelse",
    direct: false,
    heading: "Ärftlighet och sjukdomar",
  },
  livsstil: { to: "livsstil_manad", direct: true, heading: "Livsstil" },
};

// Where #1021 carried a row, when that differs from today's target: a note
// that already has this box had the row carried into it then.
const CARRIED_BY_1021_TO: Readonly<Record<string, MonthlyJournalSectionKey>> = {
  livsstil: "aktuellt_upplevelse",
};

export interface LegacyJournalRow {
  sectionKey: string;
  content: string | null;
  editedAt: string | null;
  editedByEmployeeId: string | null;
}

export interface CarriedJournalSection {
  content: string | null;
  editedAt: string | null;
  editedByEmployeeId: string | null;
}

/**
 * The new boxes' starting content from rows written under the old headings.
 * Pure; the API applies it only when it creates the new boxes, so it runs
 * once per note and never overwrites anything.
 */
export function carryLegacyMonthlySections(
  rows: readonly LegacyJournalRow[],
  opts: {
    reviewOpen: boolean;
    /** The monthly boxes the note already has (created by an earlier build). */
    existingKeys?: ReadonlySet<string>;
  },
): Partial<Record<MonthlyJournalSectionKey, CarriedJournalSection>> {
  const direct = new Map<MonthlyJournalSectionKey, LegacyJournalRow>();
  const appended = new Map<MonthlyJournalSectionKey, LegacyJournalRow[]>();
  // Legacy order, so the appended blocks read in the order they stood.
  const legacyOrder = Object.keys(MONTHLY_LEGACY_SECTION_TARGET);
  const sorted = [...rows].sort(
    (a, b) =>
      legacyOrder.indexOf(a.sectionKey) - legacyOrder.indexOf(b.sectionKey),
  );
  for (const row of sorted) {
    const target = MONTHLY_LEGACY_SECTION_TARGET[row.sectionKey];
    if (!target) continue;
    // Carried once: #1021 already put it in a box this note has.
    const earlier = CARRIED_BY_1021_TO[row.sectionKey];
    if (earlier && opts.existingKeys?.has(earlier)) continue;
    const written = row.editedAt !== null;
    const text = row.content?.trim() ?? "";
    // #992's prefill in an open review: rebuilt by the new prefill.
    if (!written && opts.reviewOpen) continue;
    if (!written && !text) continue;
    if (target.direct || (!written && row.sectionKey === "status")) {
      direct.set(target.to, row);
    } else if (text) {
      appended.set(target.to, [...(appended.get(target.to) ?? []), row]);
    }
  }

  const out: Partial<Record<MonthlyJournalSectionKey, CarriedJournalSection>> =
    {};
  const keys = new Set([...direct.keys(), ...appended.keys()]);
  for (const key of MONTHLY_JOURNAL_SECTION_KEYS) {
    if (!keys.has(key)) continue;
    const parts: string[] = [];
    const authors: LegacyJournalRow[] = [];
    const d = direct.get(key);
    if (d) {
      if (d.content?.trim()) parts.push(d.content.trim());
      if (d.editedAt) authors.push(d);
    }
    for (const row of appended.get(key) ?? []) {
      const heading = MONTHLY_LEGACY_SECTION_TARGET[row.sectionKey]!.heading;
      parts.push(
        `Tidigare under rubriken ”${heading}”:\n${row.content!.trim()}`,
      );
      if (row.editedAt) authors.push(row);
    }
    // The latest writer is the box's author; none = still untouched.
    const last = authors.sort((a, b) =>
      (a.editedAt ?? "").localeCompare(b.editedAt ?? ""),
    )[authors.length - 1];
    out[key] = {
      content: parts.length > 0 ? parts.join("\n\n") : null,
      editedAt: last?.editedAt ?? null,
      editedByEmployeeId: last?.editedByEmployeeId ?? null,
    };
  }
  return out;
}

export type MonthlyReviewPrefill = Partial<
  Record<MonthlyJournalSectionKey, JournalPrefillLine[]>
>;

// ---- the prefill (TES-978) --------------------------------------------------

// The monthly check-in's `doingWell` is a stored categorical ('yes' | 'partly'
// | 'no'); the doctor and Webdoc read Swedish. An unknown value falls back to
// itself rather than vanishing (TES-495).
export const RENEWAL_DOING_WELL_LABEL: Record<string, string> = {
  yes: "Ja, det går bra",
  partly: "Delvis",
  no: "Nej, det är jobbigt",
};

export function renewalDoingWellLabel(value: string): string {
  return RENEWAL_DOING_WELL_LABEL[value] ?? value;
}

// The questions as the app asks them (mobile `renewal.fields`), so the source
// names the question she actually answered.
export const RENEWAL_QUESTION_LABEL = {
  weight: "Vad väger du idag? (kg)",
  doingWell: "Hur går det med behandlingen?",
  sideEffects: "Har du upplevt några biverkningar?",
  questionsForDoctor: "Något du vill att läkaren tar upp?",
} as const;

/** The patient's answers to the monthly check-in (cycle_renewal_submissions). */
export interface MonthlyReviewCheckin {
  id: string;
  submittedAt: string;
  currentWeightKg: number;
  doingWell: string;
  sideEffects: string | null;
  questionsForDoctor: string | null;
}

/**
 * One weekly (F-04) answer the monthly note may read: DOCTOR or SHARED
 * (f04-field-class.ts). The API never passes a COACH or unclassified answer.
 */
export interface MonthlyReviewWeeklyItem {
  key: string;
  /** The question as she was asked it (the version she answered). */
  label: string;
  /** Her answer as the form shows it. */
  answer: string;
  /** The stored value, for trends (a number) and for "activated". */
  value: unknown;
  fieldClass: "DOCTOR" | "SHARED";
}

/** One weekly check-in's DOCTOR and SHARED answers, in the form's order. */
export interface MonthlyReviewWeeklyCheckin {
  submissionId: string;
  submittedAt: string;
  items: MonthlyReviewWeeklyItem[];
}

// TES-984 (Ö-265, Ö-267) — the monthly check-in's (F-05, `milestone`)
// questions the note pre-sorts, in the form's order. Their answers are what
// SHE reports about hunger, satiety, cravings and daily function; they go to
// "Livsstil" and to "Psykisk status och beteende", both as patient-reported.
//
// TES-985 — F-05 asks about drive, joy, initiative and desire from template
// version 4 on (`month_drive_mood_change`, Drive's MONTH_DRIVE_MOOD_CHANGE).
// "Psykisk status" reads it first; the weekly F-04's `week_drive_mood_signal`
// answers still follow, for the weeks it changed and for check-ins answered
// against an older F-05 that had no such question.
export const MONTHLY_F05_DRIVE_FIELD = "month_drive_mood_change";

// The answers "Livsstil" reads. The drive question is not one of them: it is
// a clinical question for Psykisk status, not a lifestyle report.
//
// TES-1251 (Ö-444) — from template version 5 F-05 also asks F-04's wellbeing
// question (`week_wellbeing`), because it replaces F-04 that week. It reads
// first, in the form's order, and goes where F-04's answer goes: lifestyle.
const MONTHLY_F05_LIFESTYLE_FIELDS = [
  "week_wellbeing",
  "month_hunger",
  "month_satiety",
  "month_cravings",
  "month_daily_function",
] as const;

// TES-1453 (Ö-735) — and her symptoms, for "Biverkningar".
export const MONTHLY_F05_FIELDS: readonly string[] = [
  ...MONTHLY_F05_LIFESTYLE_FIELDS,
  MONTHLY_F05_DRIVE_FIELD,
  ...F05_SYMPTOM_JOURNAL_FIELDS,
];

/** Her latest F-05 submission since the previous assessment. */
export interface MonthlyReviewMilestone {
  submissionId: string;
  submittedAt: string;
  /** Only MONTHLY_F05_FIELDS she answered. */
  items: { key: string; label: string; answer: string }[];
}

/** A medical risk flag raised since the previous assessment, and its outcome. */
export type MonthlyReviewRiskFlag = JournalRiskFlag;

/** One reading from the measurement log ("Dina värden" and the forms). */
export interface MonthlyReviewReading {
  sessionId: string;
  measuredAt: string;
  source: string;
  sourceSurface: string | null;
  value: number | null;
  systolic: number | null;
  diastolic: number | null;
  // TES-1056 — the doctor's assessment of the occasion; absent reads as
  // "patient_reported".
  reviewStatus?: string | null;
}

export interface MonthlyReviewPrefillCycle {
  id: string;
  cycleNumber: number;
  medication: string | null;
  dose: string;
  frequency: string | null;
  duration: string | null;
  prescribedAt: string | null;
}

export interface MonthlyReviewPrefillInput {
  checkin: MonthlyReviewCheckin | null;
  previousCheckin?: Pick<
    MonthlyReviewCheckin,
    "id" | "submittedAt" | "currentWeightKg"
  > | null;
  /** Oldest first, since the previous assessment. */
  weekly?: readonly MonthlyReviewWeeklyCheckin[];
  milestone?: MonthlyReviewMilestone | null;
  riskFlags?: readonly MonthlyReviewRiskFlag[];
  /** Waist readings, newest first; the first two are used. */
  waist?: readonly MonthlyReviewReading[];
  /** Blood-pressure readings, newest first; the first two are used. */
  bloodPressure?: readonly MonthlyReviewReading[];
  /** The latest resting pulse. */
  pulse?: MonthlyReviewReading | null;
  cycle: MonthlyReviewPrefillCycle | null;
  previousCycle?: MonthlyReviewPrefillCycle | null;
  /**
   * TES-1034 (Emelie, point 3) — her latest F-01 and F-02/F-02B answers, for
   * the earlier information that still bears on the renewal
   * (monthlyEarlierInfoLines). Absent or empty: nothing earlier is added.
   */
  earlier?: readonly JournalPrefillSource[];
  /**
   * TES-984 (Ö-265) — the time of the read. Waist, blood pressure and pulse
   * are prefilled only from a reading taken within the 7 days before it.
   * Without it no reading is prefilled: a reading of unknown age is not one.
   */
  now?: string;
}

// 82.5 → "82,5": the record is Swedish.
function num(value: number): string {
  return String(Math.round(value * 10) / 10).replace(".", ",");
}

function signed(delta: number, unit: string): string {
  const rounded = Math.round(delta * 10) / 10;
  if (rounded === 0) return `±0 ${unit}`;
  return `${rounded > 0 ? "+" : "-"}${num(Math.abs(rounded))} ${unit}`;
}

function day(iso: string): string {
  return iso.slice(0, 10);
}

// TES-1050 (Ö-278 b) — an answer reads in the note as its field's fixed label
// and her answer ("Besvär: Illamående"), never as "Fråga? Svar"; the source
// keeps the question verbatim. Every field this note writes has an entry in
// JOURNAL_FIELD_PHRASING (a spec holds that); for any other the question's
// own words stand in, without the instruction after it or its question mark.
function fieldLabel(
  templateKey: string,
  item: { key: string; label: string },
): string {
  // Her version's wording picks the label of a field that a later version
  // asks in other words (TES-1251).
  const phrase = journalFieldPhrase(templateKey, item.key, null, item.label);
  if (phrase) return phrase.label;
  const l = item.label;
  return (l.includes("?") ? l.slice(0, l.lastIndexOf("?")) : l).trim();
}

function fieldLine(
  templateKey: string,
  item: { key: string; label: string },
  answer: string,
): string {
  return journalPhrasedLine(fieldLabel(templateKey, item), answer);
}

const WEEKLY_KEY = "weekly_checkin";
const MILESTONE_KEY = "milestone";

const CHECKIN_LABEL = FORM_DISPLAY_NAMES.milestone; // "Månadsincheckning"
const WEEKLY_LABEL = FORM_DISPLAY_NAMES.weekly_checkin; // "Veckoincheckning"
const ORDER_LABEL = "Ordination hos Ella";

// Emelie (Ö-267): Livsstil "is always phrased as patient-reported, never as
// an objective or normal finding". The same goes for what "Psykisk status"
// is pre-sorted from: it is her account, for Kirill to assess.
const REPORTED = "Patienten anger";

function checkinSource(
  checkin: Pick<MonthlyReviewCheckin, "id" | "submittedAt">,
  q: string | null,
  answer: string | null,
): JournalPrefillSourceRef {
  return {
    kind: "form",
    label: CHECKIN_LABEL,
    // The monthly check-in is its own table, not a form template.
    templateKey: null,
    sourceId: checkin.id,
    question: q,
    answer,
    date: checkin.submittedAt,
  };
}

function weeklySource(
  week: MonthlyReviewWeeklyCheckin,
  item: Pick<MonthlyReviewWeeklyItem, "label" | "answer"> | null,
): JournalPrefillSourceRef {
  return {
    kind: "form",
    label: WEEKLY_LABEL,
    templateKey: "weekly_checkin",
    sourceId: week.submissionId,
    question: item?.label ?? null,
    answer: item?.answer ?? null,
    date: week.submittedAt,
  };
}

function milestoneSource(
  m: MonthlyReviewMilestone,
  item: { label: string; answer: string },
): JournalPrefillSourceRef {
  return {
    kind: "form",
    label: CHECKIN_LABEL,
    templateKey: "milestone",
    sourceId: m.submissionId,
    question: item.label,
    answer: item.answer,
    date: m.submittedAt,
  };
}

function readingSource(
  r: MonthlyReviewReading,
  answer: string,
): JournalPrefillSourceRef {
  return {
    kind: "measurement",
    label: measurementSourceLabel(r),
    templateKey: null,
    sourceId: r.sessionId,
    question: null,
    answer,
    date: r.measuredAt,
  };
}

function cycleSource(c: MonthlyReviewPrefillCycle): JournalPrefillSourceRef {
  return {
    kind: "prescription_cycle",
    label: ORDER_LABEL,
    templateKey: null,
    sourceId: c.id,
    question: null,
    answer: prescriptionLabel(c),
    date: c.prescribedAt,
  };
}

// A line built from several answers: `source` is the newest, `sources` all of
// them, oldest first (journal-provenance.ts).
function summaryLine(
  text: string,
  refs: readonly JournalPrefillSourceRef[],
): JournalPrefillLine {
  if (refs.length <= 1) return { text, source: refs[0] ?? null };
  return { text, source: refs[refs.length - 1]!, sources: [...refs] };
}

// "(cykel 3, förskriven 2026-09-01)" — what the decision was and when.
function cycleWhen(c: MonthlyReviewPrefillCycle): string {
  return c.prescribedAt
    ? `cykel ${c.cycleNumber}, förskriven ${day(c.prescribedAt)}`
    : `cykel ${c.cycleNumber}`;
}

function bp(r: MonthlyReviewReading): string | null {
  return r.systolic != null && r.diastolic != null
    ? `${r.systolic}/${r.diastolic} mmHg`
    : null;
}

// ---- the weekly summary (TES-984, Ö-266) ------------------------------------

// Emelie: "The note shows a compact summary of relevant changes, not a copy of
// every week." So a question is one line across the weeks — each distinct
// answer once, with the weeks it was given — and a follow-up opened in a week
// is one line for that week. Every answer stays reachable as a source.

// The DOCTOR blocks: a gate question and what opened under it. `always`: the
// gate's answer is read every week (her "Nej" on new or changed complaints
// too); otherwise only in a week that activated it (MONTHLY_WEEKLY_ACTIVATION).
interface WeeklyBlock {
  gate: string;
  always: boolean;
  followUps: readonly string[];
  /**
   * TES-1535 (Ö-802) — a week's follow-ups as running text (sentences
   * without their closing period) instead of "label: answer" pairs.
   */
  prose?: (items: readonly MonthlyReviewWeeklyItem[]) => string[];
}

// Her complaints and what opened under them, in any F-04 version (v1/v2's
// side-effect pair has no gate), written as F-04's symptom paragraph.
const COMPLAINTS: WeeklyBlock = {
  gate: F04_SYMPTOM_GATE_KEY,
  always: true,
  followUps: F04_SYMPTOM_JOURNAL_FIELDS.filter(
    (k) => k !== F04_SYMPTOM_GATE_KEY,
  ),
  prose: (items) => f04SymptomSentences(items),
};
const ADHERENCE: WeeklyBlock = {
  gate: "week_dose_adherence",
  always: false,
  followUps: ["week_dose_adherence_what"],
};
// Sorted into "Psykisk status och beteende", not "Aktuellt".
const DRIVE: WeeklyBlock = {
  gate: "week_drive_mood_signal",
  always: false,
  followUps: ["week_drive_what", "week_drive_since", "week_drive_impact"],
};

function itemOf(
  week: MonthlyReviewWeeklyCheckin,
  key: string,
): MonthlyReviewWeeklyItem | undefined {
  return week.items.find((i) => i.key === key);
}

function activated(item: MonthlyReviewWeeklyItem, block: WeeklyBlock): boolean {
  if (block.always) return true;
  const quiet = MONTHLY_WEEKLY_ACTIVATION[block.gate];
  return typeof item.value !== "string" || item.value !== quiet;
}

// "Fråga? Nej (2026-09-14, 2026-09-21); Ja (2026-09-07)" — each distinct
// answer once, in the order first given, with the weeks it was given.
function groupedLine(
  hits: readonly {
    week: MonthlyReviewWeeklyCheckin;
    item: MonthlyReviewWeeklyItem;
  }[],
  prefix = "",
): JournalPrefillLine | null {
  if (hits.length === 0) return null;
  const byAnswer = new Map<string, string[]>();
  for (const { week, item } of hits) {
    byAnswer.set(item.answer, [
      ...(byAnswer.get(item.answer) ?? []),
      day(week.submittedAt),
    ]);
  }
  const answers = [...byAnswer.entries()]
    .map(([a, days]) => `${a} (${days.join(", ")})`)
    .join("; ");
  return summaryLine(
    `${prefix}${fieldLine(WEEKLY_KEY, hits[0]!.item, answers)}`,
    hits.map(({ week, item }) => weeklySource(week, item)),
  );
}

function blockLines(
  weeks: readonly MonthlyReviewWeeklyCheckin[],
  block: WeeklyBlock,
  prefix = "",
): JournalPrefillLine[] {
  const lines: JournalPrefillLine[] = [];
  const gateHits = weeks.flatMap((week) => {
    const item = itemOf(week, block.gate);
    return item && activated(item, block) ? [{ week, item }] : [];
  });
  // One line per wording: a month that spans F-04 v6 and v7 asked the gate
  // in two different questions, and each answer keeps its own (TES-1251).
  const byLabel = new Map<string, typeof gateHits>();
  for (const hit of gateHits) {
    const label = fieldLabel(WEEKLY_KEY, hit.item);
    byLabel.set(label, [...(byLabel.get(label) ?? []), hit]);
  }
  for (const hits of byLabel.values()) {
    const gate = groupedLine(hits, prefix);
    if (gate) lines.push(gate);
  }
  for (const week of weeks) {
    // A follow-up is read only in a week whose gate opened it (a v1/v2 week
    // has no gate: its side-effect answers stand on their own).
    const g = itemOf(week, block.gate);
    if (g && !activated(g, block)) continue;
    const items = block.followUps
      .map((k) => itemOf(week, k))
      .filter((i): i is MonthlyReviewWeeklyItem => i !== undefined);
    if (items.length === 0) continue;
    const refs = items.map((i) => weeklySource(week, i));
    if (block.prose) {
      const sentences = block.prose(items);
      // Nothing to write (a declined answer, a "no" the entries do not
      // keep): her answers stay behind "Källa".
      lines.push(
        sentences.length > 0
          ? summaryLine(
              `${prefix}${day(week.submittedAt)}: ${sentences.join(". ")}.`,
              refs,
            )
          : { ...summaryLine("", refs), sourceOnly: true },
      );
      continue;
    }
    lines.push(
      summaryLine(
        `${prefix}${day(week.submittedAt)}: ${items
          .map((i) => fieldLine(WEEKLY_KEY, i, i.answer))
          .join(" · ")}`,
        refs,
      ),
    );
  }
  return lines;
}

// TES-1058 — the risk flag line is shared with the care meeting's and the
// transfer review's notes (journal-evidence-lines.ts).
const riskFlagLine = journalRiskFlagLine;

// ---- SHARED: Livsstil (TES-984, Ö-267) --------------------------------------

function sharedHits(weeks: readonly MonthlyReviewWeeklyCheckin[], key: string) {
  return weeks.flatMap((week) => {
    const item = itemOf(week, key);
    return item && item.fieldClass === "SHARED" ? [{ week, item }] : [];
  });
}

function numeric(v: unknown): number | null {
  if (typeof v === "number" && Number.isFinite(v)) return v;
  if (typeof v === "string" && v.trim() && Number.isFinite(Number(v))) {
    return Number(v);
  }
  return null;
}

function span(hits: readonly { week: MonthlyReviewWeeklyCheckin }[]): string {
  const first = day(hits[0]!.week.submittedAt);
  const last = day(hits[hits.length - 1]!.week.submittedAt);
  return first === last ? first : `${first}–${last}`;
}

// "Patienten anger – Mående överlag den senaste veckan: 2 → 4 (skala 1–5, …)"
function wellbeingLine(
  weeks: readonly MonthlyReviewWeeklyCheckin[],
): JournalPrefillLine | null {
  const hits = sharedHits(weeks, "week_wellbeing");
  if (hits.length === 0) return null;
  const values = hits.map(
    ({ item }) => numeric(item.value)?.toString() ?? item.answer,
  );
  return summaryLine(
    `${REPORTED} – ${fieldLabel(WEEKLY_KEY, hits[0]!.item)}: ${values.join(" → ")} (skala 1–5, ${WEEKLY_LABEL.toLowerCase()} ${span(hits)})`,
    hits.map(({ week, item }) => weeklySource(week, item)),
  );
}

function weightLine(
  weeks: readonly MonthlyReviewWeeklyCheckin[],
): JournalPrefillLine | null {
  const hits = sharedHits(weeks, "week_weight").filter(
    ({ item }) => numeric(item.value) !== null,
  );
  if (hits.length === 0) return null;
  const kg = hits.map(({ item }) => numeric(item.value)!);
  const trend = kg.map((v) => `${num(v)} kg`).join(" → ");
  const change =
    kg.length > 1
      ? `, förändring ${signed(kg[kg.length - 1]! - kg[0]!, "kg")}`
      : "";
  return summaryLine(
    `${REPORTED} vikt i ${WEEKLY_LABEL.toLowerCase()}en: ${trend}${change} (${span(hits)})`,
    hits.map(({ week, item }) => weeklySource(week, item)),
  );
}

function focusLine(
  weeks: readonly MonthlyReviewWeeklyCheckin[],
): JournalPrefillLine | null {
  return groupedLine(sharedHits(weeks, "week_focus"), `${REPORTED} – `);
}

function milestoneLines(
  m: MonthlyReviewMilestone | null | undefined,
  keys: readonly string[],
): JournalPrefillLine[] {
  if (!m) return [];
  return keys.flatMap((key) => {
    const item = m.items.find((i) => i.key === key);
    if (!item || !item.answer.trim()) return [];
    return [
      {
        text: `${REPORTED} – ${fieldLine(MILESTONE_KEY, item, item.answer)} (${CHECKIN_LABEL} ${day(m.submittedAt)})`,
        source: milestoneSource(m, item),
      },
    ];
  });
}

// "Patienten anger – Nya, pågående eller förändrade besvär sedan senaste
// incheckningen: Ja. Illamående: besvär just nu måttligt, förändring bättre
// (Månadsincheckning 2026-10-15)." Every answer read is a source; null when
// she gave none or nothing is written (f05SymptomSentences).
function milestoneSymptomLine(
  m: MonthlyReviewMilestone | null | undefined,
): JournalPrefillLine | null {
  if (!m) return null;
  const items = m.items.filter((i) =>
    F05_SYMPTOM_JOURNAL_FIELDS.includes(i.key),
  );
  const sentences = f05SymptomSentences(items);
  if (sentences.length === 0) return null;
  return summaryLine(
    `${REPORTED} – ${sentences.join(". ")} (${CHECKIN_LABEL} ${day(m.submittedAt)}).`,
    items.map((i) => milestoneSource(m, i)),
  );
}

function fresh(r: MonthlyReviewReading, now: string | undefined): boolean {
  if (!now) return false;
  const elapsed = Date.parse(now) - Date.parse(r.measuredAt);
  // A reading from the future (a device clock) is wrong, not fresh — the
  // same rule as the forms' 7-day suggestion (measurement.service).
  return (
    elapsed >= 0 && elapsed <= MEASUREMENT_SUGGESTION_WINDOW_DAYS * 86_400_000
  );
}

/**
 * The monthly note's boxes as her check-ins, her readings and the cycle would
 * fill them, each line with its source. Pure: the API decides WHEN to apply it
 * (only to a box nobody has written, only while the review is open).
 *
 *   Aktuellt/patientens upplevelse — the monthly check-in (how it goes, her
 *     questions), then the DOCTOR weekly answers since the previous
 *     assessment as a compact summary (complaints incl. her "Nej", adherence
 *     when activated), and the medical risk flags with their outcome
 *   Vikt — the check-in's weight against the previous check-in
 *   "Midjemått", "Blodtryck" — a reading from the last 7 days, with date and
 *     source
 *   Livsstil — what she reports: the SHARED weekly answers (what worked,
 *     wellbeing trend, weight trend) and F-05's hunger, satiety, cravings and
 *     daily function
 *   Psykisk status och beteende — F-05's drive/joy question, the weekly
 *     drive/joy/initiative/desire answers when activated, and the same four
 *     F-05 answers
 *   Biverkningar — the check-in's side effects
 *   Aktuell behandling och dos — the order in force, and the previous one if
 *     it differs
 *
 * The doctor's three ("Bedömning", "Åtgärd och beslut", "Nästa uppföljning")
 * are never filled (MONTHLY_DOCTOR_ONLY_SECTIONS). COACH answers are never
 * input (the API does not pass them), and this function reads DOCTOR and
 * SHARED answers only where listed above.
 *
 * TES-978: "Saknad information får aldrig formuleras som ett normalt fynd."
 * Anything she did not answer, and any reading that does not exist, is LEFT
 * OUT — never "inga", "nej" or "normal". A box with nothing to say is absent
 * from the result and stays empty; Livsstil and Psykisk status then read
 * "Saknas/ej bedömd" when assembled.
 */
export function prefillMonthlyReviewJournal(
  input: MonthlyReviewPrefillInput,
): MonthlyReviewPrefill {
  const out: MonthlyReviewPrefill = {};
  const { checkin, previousCheckin, cycle, previousCycle } = input;
  const weeks = input.weekly ?? [];

  // Aktuellt/patientens upplevelse
  const current: JournalPrefillLine[] = [];
  if (checkin) {
    current.push({
      text: `${CHECKIN_LABEL} ${day(checkin.submittedAt)}`,
      source: checkinSource(checkin, null, null),
    });
    const doing = renewalDoingWellLabel(checkin.doingWell);
    current.push({
      text: journalAnswerLine("Hur det går", doing),
      source: checkinSource(checkin, RENEWAL_QUESTION_LABEL.doingWell, doing),
    });
    const q = checkin.questionsForDoctor?.trim();
    if (q) {
      current.push({
        text: journalAnswerLine("Frågor till läkaren", q),
        source: checkinSource(
          checkin,
          RENEWAL_QUESTION_LABEL.questionsForDoctor,
          q,
        ),
      });
    }
  }
  const weekly = [
    ...blockLines(weeks, COMPLAINTS),
    ...blockLines(weeks, ADHERENCE),
    ...(input.riskFlags ?? []).map(riskFlagLine),
  ];
  if (weekly.length > 0) {
    if (current.length > 0) current.push({ text: "", source: null });
    // The heading names the weeks it sums up, and they are its sources.
    const read = weeks.filter((w) =>
      w.items.some((i) => i.fieldClass === "DOCTOR"),
    );
    current.push(
      read.length > 0
        ? summaryLine(
            `${WEEKLY_LABEL}ar sedan föregående månadsbedömning (${read.length} st, ${span(
              read.map((week) => ({ week })),
            )}):`,
            read.map((week) => weeklySource(week, null)),
          )
        : { text: "Sedan föregående månadsbedömning:", source: null },
    );
    current.push(...weekly);
  }
  if (current.length > 0) out.aktuellt_upplevelse = current;

  // Vikt
  if (checkin) {
    const kg = `${num(checkin.currentWeightKg)} kg`;
    const lines: JournalPrefillLine[] = [
      {
        text: `${kg} (${CHECKIN_LABEL} ${day(checkin.submittedAt)})`,
        source: checkinSource(checkin, RENEWAL_QUESTION_LABEL.weight, kg),
      },
    ];
    if (previousCheckin) {
      const prevKg = `${num(previousCheckin.currentWeightKg)} kg`;
      lines.push({
        text: `Föregående ${CHECKIN_LABEL.toLowerCase()}: ${prevKg} (${day(
          previousCheckin.submittedAt,
        )}), förändring ${signed(
          checkin.currentWeightKg - previousCheckin.currentWeightKg,
          "kg",
        )}`,
        source: checkinSource(
          previousCheckin,
          RENEWAL_QUESTION_LABEL.weight,
          prevKg,
        ),
      });
    }
    out.vikt = lines;
  }

  // "Midjemått" — Ö-265: only a reading from the last 7 days is prefilled; the
  // one before it is shown beside it for the change.
  const [waist, prevWaist] = (input.waist ?? []).filter((r) => r.value != null);
  if (waist && fresh(waist, input.now)) {
    const cm = `${num(waist.value!)} cm`;
    const lines: JournalPrefillLine[] = [
      {
        text: `${cm} (${day(waist.measuredAt)}, ${measurementSourceLabel(waist)})`,
        source: readingSource(waist, cm),
      },
    ];
    if (prevWaist) {
      const prevCm = `${num(prevWaist.value!)} cm`;
      lines.push({
        text: `Föregående: ${prevCm} (${day(prevWaist.measuredAt)}), förändring ${signed(
          waist.value! - prevWaist.value!,
          "cm",
        )}`,
        source: readingSource(prevWaist, prevCm),
      });
    }
    out.midjematt = lines;
  }

  // Blodtryck (with the resting pulse, as the header shows them), under the
  // same 7-day rule. TES-1056 — each line carries the date, the source and
  // the doctor's assessment (bloodPressurePulseProvenance), never a reading
  // of what the value means.
  const [pressure, prevPressure] = (input.bloodPressure ?? []).filter(
    (r) => bp(r) !== null,
  );
  const freshPressure =
    pressure && fresh(pressure, input.now) ? pressure : null;
  const pulse =
    input.pulse?.value != null && fresh(input.pulse, input.now)
      ? input.pulse
      : null;
  const pressureLines: JournalPrefillLine[] = [];
  if (freshPressure) {
    const value = bp(freshPressure)!;
    pressureLines.push({
      text: `${value} (${bloodPressurePulseProvenance(freshPressure)})`,
      source: readingSource(freshPressure, value),
    });
  }
  if (pulse) {
    const value = `${Math.round(pulse.value!)} slag/min`;
    pressureLines.push({
      text:
        freshPressure && pulse.sessionId === freshPressure.sessionId
          ? `Vilopuls: ${value}`
          : `Vilopuls: ${value} (${bloodPressurePulseProvenance(pulse)})`,
      source: readingSource(pulse, value),
    });
  }
  if (freshPressure && prevPressure) {
    const value = bp(prevPressure)!;
    pressureLines.push({
      text: `Föregående: ${value} (${bloodPressurePulseProvenance(prevPressure)})`,
      source: readingSource(prevPressure, value),
    });
  }
  if (pressureLines.length > 0) out.blodtryck = pressureLines;

  // Livsstil — what she reports; nothing here is a finding.
  const lifestyle = [
    focusLine(weeks),
    wellbeingLine(weeks),
    weightLine(weeks),
  ].filter((l): l is JournalPrefillLine => l !== null);
  lifestyle.push(
    ...milestoneLines(input.milestone, MONTHLY_F05_LIFESTYLE_FIELDS),
  );
  if (lifestyle.length > 0) out.livsstil_manad = lifestyle;

  // Psykisk status och beteende — pre-sorted for Kirill to edit and assess:
  // drive/joy/initiative/desire as she answered it in F-05 (TES-985) and
  // in the weeks it changed, then daily function and the appetite signals
  // from F-05.
  const psych = [
    ...milestoneLines(input.milestone, [MONTHLY_F05_DRIVE_FIELD]),
    ...blockLines(weeks, DRIVE, `${REPORTED} – `),
    ...milestoneLines(input.milestone, [
      "month_daily_function",
      "month_hunger",
      "month_satiety",
      "month_cravings",
    ]),
  ];
  if (psych.length > 0) out.psykisk_status = psych;

  // Biverkningar
  const sideEffectLines: JournalPrefillLine[] = [];
  const sideEffects = checkin?.sideEffects?.trim();
  if (checkin && sideEffects) {
    sideEffectLines.push({
      text: `${sideEffects} (${CHECKIN_LABEL} ${day(checkin.submittedAt)})`,
      source: checkinSource(
        checkin,
        RENEWAL_QUESTION_LABEL.sideEffects,
        sideEffects,
      ),
    });
  }
  // TES-1453 (Ö-735) — F-05's symptom answers as one paragraph.
  const f05Symptoms = milestoneSymptomLine(input.milestone);
  if (f05Symptoms) sideEffectLines.push(f05Symptoms);
  if (sideEffectLines.length > 0) out.biverkningar = sideEffectLines;

  // Aktuell behandling och dos
  if (cycle && cycle.dose.trim()) {
    const meds: JournalPrefillLine[] = [
      {
        text: `${ORDER_LABEL}: ${prescriptionLabel(cycle)} (${cycleWhen(cycle)})`,
        source: cycleSource(cycle),
      },
    ];
    // The previous decision, only when it was a different one: "unchanged"
    // is what the first line already says.
    if (previousCycle && previousCycle.dose.trim()) {
      const before = prescriptionLabel(previousCycle);
      if (before !== prescriptionLabel(cycle)) {
        meds.push({
          text: `Föregående ordination: ${before} (${cycleWhen(previousCycle)})`,
          source: cycleSource(previousCycle),
        });
      }
    }
    out.aktuell_behandling_dos = meds;
  }

  // TES-1034 — earlier information last, after what this month says, and
  // only what this month does not already say.
  const earlier = monthlyEarlierInfoLines(input.earlier ?? [], out);
  for (const [key, lines] of Object.entries(earlier) as [
    MonthlyJournalSectionKey,
    JournalPrefillLine[],
  ][]) {
    const box = out[key] ?? [];
    if (box.length > 0) box.push({ text: "", source: null });
    box.push(...lines);
    out[key] = box;
  }

  return out;
}

// ---- earlier information (TES-1034, Emelie point 3) -----------------------
//
// The renewal's note is compiled from, among the rest, earlier patient
// information while it is still relevant to the decision at hand (point 3,
// fourth source). Which earlier answers are relevant is read from
// JOURNAL_PREFILL_MAP, the map the care meeting's note uses: what it places
// under the meeting's history-and-diseases heading and its medicines
// heading. The monthly note has neither heading (Ö-265 kept the history
// heading out of it), so the history goes under the current-state heading,
// which Ö-265 names for diagnoses, and the medicines under the treatment
// heading.
//
// Scope, deliberately narrow:
//   - F-02/F-02B (`intake`) and F-01 (`f01`) only, her latest submission of
//     each. From F-01 only her experience of weight medication: the rest of
//     the quiz (how long she has tried, what she tried, lifestyle) was
//     context for the first decision, not for a monthly renewal.
//   - A plain "no" is left out here. At the first meeting a negation is a
//     finding; months later a list of old negations is noise, and the weekly
//     and monthly check-ins ask for new or changed complaints now.
//   - Nothing the month's own lines already state is repeated: an answer
//     that already appears in the same box is dropped, and so is the same
//     answer given in both forms (the newer form is kept).
//   - Every group is headed with the form and the date she answered it, so it
//     reads as earlier information, never as this month's.
// The weekly check-in's coach and private answers never reach this function:
// its inputs are F-01 and F-02 only.

/** Where the meeting's headings land in the monthly note, for earlier info. */
export const MONTHLY_EARLIER_INFO_TARGET: Partial<
  Record<JournalSectionKey, MonthlyJournalSectionKey>
> = {
  arftlighet_sjukdomar: "aktuellt_upplevelse",
  aktuella_lakemedel: "aktuell_behandling_dos",
};

/** The forms read for earlier information, newest submission of each. */
export const MONTHLY_EARLIER_INFO_TEMPLATE_KEYS = ["intake", "f01"] as const;

/**
 * F-01's field that stays relevant to a renewal: her experience of weight
 * medication (on it today, used before with or without effect). Not
 * `knownMedications`, which the meeting's map also places under medicines:
 * that question asks which medicines she has heard of, not which she takes.
 */
export const MONTHLY_EARLIER_F01_FIELDS: readonly string[] = [
  "weightMedHistory",
];

const PLAIN_NEGATIVE = /^(nej|inga|ingen|inget)\.?$/i;

// Answers are compared on their words, not their punctuation: F-02's
// medicine list and F-01's free text write the same medicine differently.
// An answer this short or this generic is not compared across questions:
// two different questions can both be answered with a plain yes.
function comparable(answer: string): string | null {
  const a = answer
    .toLowerCase()
    .replace(/[(),.;:]/g, " ")
    .replace(/\s+/g, " ")
    .trim();
  if (a.length < 4 || /^(ja|nej)\b/.test(a)) return null;
  return a;
}

/**
 * The earlier F-01/F-02 lines for the monthly note, per box, each group
 * headed with its form and date. `stated` is the month's own prefill, read
 * only to leave out what it already says. Pure.
 */
export function monthlyEarlierInfoLines(
  sources: readonly JournalPrefillSource[],
  stated: MonthlyReviewPrefill = {},
): MonthlyReviewPrefill {
  const out: MonthlyReviewPrefill = {};
  const seen = new Map<MonthlyJournalSectionKey, Set<string>>();
  const alreadyStated = (key: MonthlyJournalSectionKey, a: string) =>
    Boolean(seen.get(key)?.has(a)) ||
    (stated[key] ?? []).some((l) => comparable(l.text)?.includes(a));

  // Newest form first, so a repeated answer is kept where it is newest.
  const ordered = sources
    .filter((s) =>
      (MONTHLY_EARLIER_INFO_TEMPLATE_KEYS as readonly string[]).includes(
        s.templateKey,
      ),
    )
    .sort((a, b) => (b.submittedAt ?? "").localeCompare(a.submittedAt ?? ""));

  for (const source of ordered) {
    const narrowed: JournalPrefillSource =
      source.templateKey === "f01"
        ? {
            ...source,
            summary: {
              ...source.summary,
              sections: source.summary.sections.map((s) => ({
                ...s,
                items: s.items.filter((i) =>
                  MONTHLY_EARLIER_F01_FIELDS.includes(i.key),
                ),
              })),
            },
          }
        : source;
    // Undated: the group's heading carries the form and day once.
    const byMeetingKey = prefillJournalSectionLines({
      sources: [narrowed],
      undated: true,
    });
    const label = source.formLabel ?? source.templateKey;
    const when = source.submittedAt ? ` ${day(source.submittedAt)}` : "";

    for (const [meetingKey, target] of Object.entries(
      MONTHLY_EARLIER_INFO_TARGET,
    ) as [JournalSectionKey, MonthlyJournalSectionKey][]) {
      const kept: JournalPrefillLine[] = [];
      for (const line of byMeetingKey[meetingKey] ?? []) {
        // TES-1050 — only what the meeting's note writes; its "Källa"-only
        // answers stay out of the monthly note too.
        if (line.sourceOnly) continue;
        const ref = line.source;
        const answer = ref?.answer ?? "";
        if (!ref?.question || isDeclinedAnswer(answer)) continue;
        if (PLAIN_NEGATIVE.test(answer.trim())) continue;
        // A follow-up joined to its "Ja" is compared on what it says.
        const said =
          line.sources && line.sources.length > 1
            ? line.sources
                .slice(1)
                .map((r) => r.answer ?? "")
                .join(" ")
            : answer;
        const a = comparable(said);
        if (a) {
          if (alreadyStated(target, a)) continue;
          if (!seen.has(target)) seen.set(target, new Set());
          seen.get(target)!.add(a);
        }
        kept.push(line);
      }
      if (kept.length === 0) continue;
      const box = out[target] ?? [];
      if (box.length > 0) box.push({ text: "", source: null });
      box.push(
        {
          text: `Tidigare uppgifter (${label}${when}):`,
          source: { ...kept[0]!.source!, question: null, answer: null },
        },
        ...kept,
      );
      out[target] = box;
    }
  }
  return out;
}
